import * as React from "react";
import { AutoCompleteInput, Checkbox, ColorPicker, Input, Select } from "uxp/components";
import { applyAtPath, findAvailableFields, IUIPropertyDefinition } from "../../../common/common";
import { Property, PropertyContainer, PropertyContainerTitle, PropertyGroup, PropertyGroupTitle, PropertyTitle } from "../../../common/ui-props/common";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bindContainerId } from "../../../../util";
import { WidgetDesignerPillInput } from "../../../common/pill-input/PillInput";
import { ChartSorting } from "./DonutChartTemplate";
import { Conditional } from "../../../common/ConditionalComponent";
import ActionEditorLink, { IAction } from "../../../common/action-editor/ActionEditor";
import classNames from "classnames";
import jp from 'jsonpath'

export interface IDonutChartProps {
    labelField: string,
    valueField: string,
    sorting: ChartSorting
    customTooltipText?: string,
    customLabelText?: string,
    showPercentage?: boolean,
    legend: boolean,
    colors: string[]
    labelColors?: { label: string, color: string }[],
    mapLabelsToColors?: boolean
    chartSize: number,
    type: 'donut' | 'pie',
    refreshChartAutoMatically?: boolean,
    refreshInsterval: number,
    channel?: string,
    useColorsForLabels: boolean,
    onClickChart?: IAction
}


export function DonutChartProperty(title: string, defaults: IDonutChartProps): IUIPropertyDefinition<IDonutChartProps> {
    return {
        defaults: defaults,
        render: (props, uxpContext, contextData, containerId) => {

            let _props = props.uiProps
            if (!_props?.labelColors) _props.labelColors = []
            if (_props?.refreshChartAutoMatically == undefined) _props.refreshChartAutoMatically = false

            let possibleValues = {};
            findAvailableFields(props.parentUIProps.source, props.parentUIProps.datasources, possibleValues);
            let pv = Object.keys(possibleValues);
            pv.sort();

            const customContextValues = [
                {
                    label: 'Calculated values',
                    fields: [
                        { label: 'percentage', value: '$.calculated.percentage' },
                        { label: 'value', value: `$.calculated.value` }
                    ]
                }
            ]

            const mapLabelsToColors = _props.mapLabelsToColors


            return <>
                <PropertyContainer>
                    <PropertyContainerTitle>{title}</PropertyContainerTitle>
                    <PropertyGroup>
                        <Property>
                            <PropertyTitle>Chart Type</PropertyTitle>
                            <div className="donut-chart-type-selector-container">
                                <div className={classNames("type", { 'selected': _props.type == 'pie' })} onClick={() => {
                                    jp.apply(_props, '$.type', () => ('pie'))
                                    props.onChange(_props)
                                }}>
                                    <FontAwesomeIcon icon={['fas', 'chart-pie']} />
                                </div>

                                <div className={classNames("type", { 'selected': _props.type == 'donut' })} onClick={() => {
                                    jp.apply(_props, '$.type', () => ('donut'))
                                    props.onChange(_props)
                                }}>
                                    <FontAwesomeIcon icon={['fas', 'dot-circle']} />
                                </div>
                            </div>
                        </Property>
                        <Property>
                            <PropertyTitle>Chart Size (%)</PropertyTitle>
                            <Input
                                type="number"
                                value={_props.chartSize?.toString() || ''}
                                onChange={s => {
                                    jp.apply(_props, '$.chartSize', () => (Number(s) || 0))
                                    props.onChange(_props)
                                }}
                            />
                        </Property>
                        <Property id={bindContainerId('value-field', containerId)}>
                            <PropertyTitle>Value Field</PropertyTitle>
                            <AutoCompleteInput
                                value={_props.valueField || ''}
                                onChange={s => {
                                    jp.apply(_props, '$.valueField', () => (s))
                                    props.onChange(_props)
                                }}
                                className="widget-designer-input"
                                options={pv}
                            />

                        </Property>

                        <Property id={bindContainerId('label-field', containerId)}>
                            <PropertyTitle>Label Field</PropertyTitle>
                            <AutoCompleteInput
                                value={_props.labelField || ''}
                                onChange={s => {
                                    jp.apply(_props, '$.labelField', () => (s))
                                    props.onChange(_props)
                                }}
                                className="widget-designer-input"
                                options={pv}
                            />

                        </Property>

                        <Property>
                            <PropertyTitle>Sorting</PropertyTitle>
                            <Select
                                options={[
                                    { label: 'None', value: 'none' },
                                    { label: 'By Label', value: 'by-label' },
                                    { label: 'By Value', value: 'by-value' },
                                ]}
                                selected={_props.sorting || ''}
                                onChange={s => {
                                    jp.apply(_props, '$.sorting', () => (s))
                                    props.onChange(_props)
                                }}
                            />

                        </Property>

                        <Property>
                            <PropertyTitle>Custom Tooltip Text</PropertyTitle>
                            <WidgetDesignerPillInput
                                value={_props.customTooltipText}
                                onChange={s => {
                                    props.onChange({ ..._props, customTooltipText: s })
                                }}
                                customContextValues={customContextValues}
                                contextData={contextData}
                            />
                        </Property>

                        <Property>
                            <PropertyTitle>Custom Label (On Chart)</PropertyTitle>
                            <WidgetDesignerPillInput
                                value={_props.customLabelText}
                                onChange={s => {
                                    props.onChange({ ..._props, customLabelText: s })
                                }}
                                customContextValues={customContextValues}
                                contextData={contextData}
                            />
                        </Property>

                        <Property>
                            <Checkbox
                                checked={_props?.showPercentage}
                                onChange={s => {
                                    jp.apply(_props, '$.showPercentage', () => (s))
                                    props.onChange(_props)
                                }}
                                label="Show percentage as label"
                            />

                        </Property>

                        <Property>
                            <Checkbox
                                checked={_props?.legend}
                                onChange={s => {
                                    jp.apply(_props, '$.legend', () => (s))
                                    props.onChange(_props)
                                }}
                                label="Show Legend"
                            />

                        </Property>
                        <Property>
                            <Checkbox
                                checked={!!_props?.useColorsForLabels}
                                onChange={s => {
                                    let p = applyAtPath(_props, '$.useColorsForLabels', () => (s))
                                    props.onChange(p)
                                }}
                                label="Use colored labels"
                            />

                        </Property>

                        <Property>
                            <PropertyTitle>On click an item </PropertyTitle>
                            <ActionEditorLink
                                uiProps={props.parentUIProps}
                                designer={props.designer}
                                uxpContext={uxpContext}
                                action={_props?.onClickChart || null}
                                onChange={s => {
                                    if (!_props?.onClickChart) _props.onClickChart = null
                                    jp.apply(_props, '$.onClickChart', () => (s))
                                    props.onChange(_props)
                                }}
                                contextData={contextData}
                            />
                        </Property>

                    </PropertyGroup>
                </PropertyContainer>

                <PropertyContainer>
                    <PropertyContainerTitle>Colors</PropertyContainerTitle>

                    <PropertyGroup>
                        <Property>
                            <Checkbox
                                checked={_props?.mapLabelsToColors}
                                onChange={s => {
                                    props.onChange(Object.assign({}, _props, { mapLabelsToColors: s }))
                                }}
                                label="Match colors with Labels"
                            />
                        </Property>
                    </PropertyGroup>

                    <PropertyGroup>
                        <div className="wd-ui-property-group-title" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            Colors

                            <div className="plus" style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '14px',
                                cursor: 'pointer'
                            }}
                                onClick={() => {

                                    if (mapLabelsToColors) {
                                        let _colors = _props.labelColors || []
                                        _colors.unshift({ label: '', color: '' })
                                        jp.apply(_props, `$.labelColors`, () => (_colors))
                                        props.onChange(_props)
                                    }
                                    else {
                                        let _colors = _props?.colors || []
                                        _colors.unshift('')

                                        jp.apply(_props, `$.colors`, () => (_colors))
                                        props.onChange(_props)
                                    }
                                }}
                            >
                                <FontAwesomeIcon icon={['fas', 'plus']} />
                            </div>

                        </div>

                        <Property>
                            <PropertyTitle>Colors</PropertyTitle>

                            <div className="wd-gauge-config-colors">
                                {mapLabelsToColors ? <>
                                    {_props?.labelColors?.map((s, i) => {
                                        return <div className="row">

                                            <div className="color">
                                                <ColorPicker
                                                    color={s.color}
                                                    onChange={(s) => {
                                                        jp.apply(_props, `$.labelColors[${i}].color`, () => (s))
                                                        props.onChange(_props)
                                                    }}
                                                    position='right'
                                                    className='color-input'
                                                />

                                            </div>
                                            <div className="stop-at">
                                                <Input
                                                    value={s.label}
                                                    onChange={s => {
                                                        jp.apply(_props, `$.labelColors[${i}].label`, () => (s))
                                                        props.onChange(_props)
                                                    }}
                                                />
                                            </div>



                                            <div className="close"
                                                onClick={() => {
                                                    let _colors = _props?.labelColors || []
                                                    _colors.splice(i, 1)

                                                    jp.apply(_props, `$.labelColors`, () => (_colors))
                                                    props.onChange(_props)
                                                }}
                                            >
                                                <FontAwesomeIcon icon={['fas', 'trash']} className='delete' />
                                            </div>
                                        </div>
                                    })}
                                </>
                                    : <>
                                        {_props?.colors?.map((s, i) => {
                                            return <div className="row">

                                                <div className="color">
                                                    <ColorPicker
                                                        color={s}
                                                        onChange={(s) => {
                                                            jp.apply(_props, `$.colors[${i}]`, () => (s))
                                                            props.onChange(_props)
                                                        }}
                                                        // className='color-input'
                                                        position='right'
                                                    />

                                                </div>

                                                <div className="close"
                                                    onClick={() => {
                                                        let _colors = _props?.colors || []
                                                        _colors.splice(i, 1)

                                                        jp.apply(_props, `$.colors`, () => (_colors))
                                                        props.onChange(_props)
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={['fas', 'trash']} className='delete' />
                                                </div>
                                            </div>
                                        })}
                                    </>}
                            </div>

                        </Property>

                    </PropertyGroup>
                </PropertyContainer>

                <PropertyContainer>
                    <PropertyContainerTitle>Auto Refresh</PropertyContainerTitle>

                    <PropertyGroup>
                        <Property>
                            <Checkbox
                                checked={_props?.refreshChartAutoMatically || false}
                                onChange={s => {
                                    jp.apply(_props, '$.refreshChartAutoMatically', () => (s))
                                    props.onChange(_props)
                                }}
                                label="Enable Automatic refresh"
                            />
                        </Property>

                        <Conditional visible={_props?.refreshChartAutoMatically}>

                            <PropertyGroupTitle>Reresh Periodically</PropertyGroupTitle>

                            <Property>
                                <PropertyTitle>Refresh Interval (ms)</PropertyTitle>
                                <Input
                                    type="number"
                                    value={_props?.refreshInsterval?.toString() || '0'}
                                    onChange={s => {
                                        let num = Number(s) || 0
                                        jp.apply(_props, '$.refreshInsterval', () => (num))
                                        props.onChange(_props)
                                    }}
                                />
                            </Property>

                            <PropertyGroupTitle>Refresh when a message received</PropertyGroupTitle>

                            <Property>
                                <PropertyTitle>Channel Name</PropertyTitle>
                                <Input
                                    value={_props?.channel || ''}
                                    onChange={s => {
                                        jp.apply(_props, '$.channel', () => (s))
                                        props.onChange(_props)
                                    }}
                                />

                            </Property>

                        </Conditional>

                    </PropertyGroup>
                </PropertyContainer>
            </>
        }
    };
}
